import React, { memo } from 'react';
import {
  Box,
  Typography,
  Avatar,
  Tooltip,
  IconButton,
  Stack,
  CircularProgress,
} from '@material-ui/core';
import { Person, AttachFile, Edit } from '@material-ui/icons';
import Lottie from 'lottie-react';
import robotIcon from 'src/assets/Ai Robot Vector Art.json';
import { stripChatAsterisks } from '../utils/chatText';
import BlueprintChatPreview from './BlueprintChatPreview';
import ChatTicketList from './ChatTicketList';

const ChatMessage = ({
  msg,
  theme,
  maximized,
  isDarkMode,
  lastUserMessageId,
  sendMessage,
  onPreviewImage,
  onEditLastPrompt,
  userProfilePic,
  userName,
}) => {
  const isAi = msg.role === 'ai';
  const isLoading = isAi && msg.status === 'loading';

  return (
    <Box
      sx={{
        py: 2,
        px: maximized ? { xs: 2, md: 10 } : 2,
        display: 'flex',
        flexDirection: isAi ? 'row' : 'row-reverse',
        gap: 1.5,
        width: '100%',
        alignItems: 'flex-start',
      }}
    >
      <Box sx={{ width: 32, height: 32, flexShrink: 0, mt: 0.5 }}>
        {isAi ? (
          <Lottie animationData={robotIcon} loop={true} />
        ) : (
          <Avatar
            src={userProfilePic || undefined}
            alt={userName || 'User'}
            sx={{
              bgcolor: theme.palette.secondary.main,
              width: 32,
              height: 32,
              fontSize: '0.875rem',
            }}
          >
            {userName?.charAt(0)?.toUpperCase() || <Person fontSize="small" />}
          </Avatar>
        )}
      </Box>

      <Box
        sx={{
          maxWidth: '75%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: isAi ? 'flex-start' : 'flex-end',
        }}
      >
        {msg.files && msg.files.length > 0 && (
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1,
              mb: 1,
              width: '100%',
              justifyContent: isAi ? 'flex-start' : 'flex-end',
            }}
          >
            {msg.files.map((file, fIdx) =>
              file.url ? (
                <Box
                  key={fIdx}
                  component="img"
                  src={file.url}
                  onClick={() => onPreviewImage(file.url)}
                  sx={{
                    maxWidth: msg.files.length > 1 ? 140 : 240,
                    maxHeight: msg.files.length > 1 ? 140 : 240,
                    borderRadius: '12px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                    border: isDarkMode
                      ? '1px solid rgba(255,255,255,0.1)'
                      : '1px solid rgba(0,0,0,0.05)',
                    cursor: 'pointer',
                    transition: 'transform 0.2s',
                    '&:hover': { transform: 'scale(1.02)' },
                  }}
                />
              ) : (
                <Box
                  key={fIdx}
                  sx={{
                    p: 1.5,
                    bgcolor: isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    border: `1px solid ${
                      isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'
                    }`,
                  }}
                >
                  <AttachFile sx={{ fontSize: 18 }} />
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>
                    {file.name}
                  </Typography>
                </Box>
              )
            )}
          </Box>
        )}
        <Box
          sx={{
            bgcolor: isDarkMode ? '#2f2f2f' : '#f4f4f4',
            color: isDarkMode ? '#ececf1' : '#374151',
            p: 1.5,
            px: 2,
            borderRadius: isAi ? '4px 20px 20px 20px' : '20px 20px 4px 20px',
            boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
            transition: 'all 0.2s',
          }}
        >
          {isLoading ? (
            <Stack spacing={1}>
              <Stack direction="row" spacing={1} alignItems="center">
                <CircularProgress
                  size={16}
                  sx={{ color: isDarkMode ? '#ececf1' : '#374151' }}
                />
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: '0.9rem',
                    fontWeight: 500,
                    color: isDarkMode ? '#ececf1' : '#374151',
                  }}
                >
                  {msg.currentStep || 'Thinking…'}
                </Typography>
              </Stack>
              {(msg.steps || []).slice(0, -1).map((step) => (
                <Typography
                  key={step}
                  variant="caption"
                  sx={{ opacity: 0.65, fontSize: '0.78rem', pl: 3 }}
                >
                  ✓ {step}
                </Typography>
              ))}
            </Stack>
          ) : (
            <>
              {Boolean(msg.content?.trim()) && (
                <Typography
                  variant="body1"
                  sx={{
                    lineHeight: 1.5,
                    whiteSpace: 'pre-wrap',
                    fontSize: '0.92rem',
                  }}
                >
                  {stripChatAsterisks(msg.content)}
                </Typography>
              )}
              {msg.blueprintResult && (
                <BlueprintChatPreview data={msg.blueprintResult} isDarkMode={isDarkMode} />
              )}
              {isAi && msg.tickets && (
                <ChatTicketList tickets={msg.tickets} isDarkMode={isDarkMode} />
              )}
            </>
          )}
        </Box>
        {!isAi &&
          msg.id === lastUserMessageId &&
          !sendMessage &&
          Boolean((msg.content || '').trim()) && (
            <Tooltip title="Edit prompt" arrow placement="left">
              <IconButton
                size="small"
                onClick={() => onEditLastPrompt(msg.id)}
                aria-label="Edit prompt"
                sx={{
                  mt: 0.5,
                  mr: 0.25,
                  p: 0.5,
                  color: isDarkMode ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.45)',
                  '&:hover': {
                    color: isDarkMode ? '#fff' : '#111',
                    bgcolor: isDarkMode
                      ? 'rgba(255,255,255,0.06)'
                      : 'rgba(0,0,0,0.04)',
                  },
                }}
              >
                <Edit sx={{ fontSize: 15 }} />
              </IconButton>
            </Tooltip>
          )}
        {isAi &&
          !isLoading &&
          msg.generatedTime != null &&
          Number.isFinite(Number(msg.generatedTime)) && (
            <Typography
              component="div"
              className="generated-time"
              sx={{
                fontSize: '12px',
                color: isDarkMode ? 'rgba(255,255,255,0.45)' : '#8a9bb8',
                mt: 0.5,
                ml: 0.5,
                lineHeight: 1.3,
              }}
            >
              Generated in {Number(msg.generatedTime).toFixed(2)}s
            </Typography>
          )}
      </Box>
    </Box>
  );
};

export default memo(ChatMessage);
